import { atom, useSetAtom } from "jotai";
import { useEffect } from "react";

export const keyboardUserAtom = atom(false);

const INPUT_TYPES_WHITE_LIST = [
  "text",
  "search",
  "email",
  "url",
  "tel",
  "password",
  "number",
  "date",
  "datetime-local",
  "month",
  "time",
  "week",
];

function isEditable(el: Element | null): boolean {
  if (!el || el instanceof Window) return false;
  if ((el as HTMLElement).isContentEditable) return true;

  const tag = el.tagName?.toLowerCase();
  if (tag === "textarea") return true;

  if (tag === "input") {
    const type = (el as HTMLInputElement).type?.toLowerCase();
    const textLike = !type || INPUT_TYPES_WHITE_LIST.includes(type);

    return textLike;
  }

  return false;
}

/**
 * Use Is Keyboard User
 *
 * this has been adapted from https://github.com/dipunm/use-focus-visible/blob/master/src/FocusVisibleManager.tsx
 *
 * It will watch user movements and toggle whether user is navigating via mouse or keyboard
 *
 * Instead of using it on all buttons and links, it toggles the class keyboard-user on body
 *
 * This way we can add custom focus states to all of our themed elements
 */
export function useIsKeyboardUser() {
  const setKeyboardUser = useSetAtom(keyboardUserAtom);

  useEffect(() => {
    function onPointerDown() {
      document.body.classList.remove("keyboard-user");
      setKeyboardUser(false);
    }

    /**
     * When the polyfill first loads, assume the user is in keyboard modality.
     * If any event is received from a pointing device (e.g. mouse, pointer,
     * touch), turn off keyboard modality.
     * This accounts for situations where focus enters the page from the URL bar.
     */
    function onInitialPointerMove(e: Event) {
      // Work around a Safari quirk that fires a mousemove on <html> whenever the
      // window blurs, even if you're tabbing out of the page. ¯\_(ツ)_/¯
      const target = e?.target as Element;
      if (target?.nodeName?.toLowerCase() === "html") return;

      document.body.classList.remove("keyboard-user");
      setKeyboardUser(false);
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      removeInitialPointerMoveListeners();
    }

    /**
     * Add a group of listeners to detect usage of any pointing devices.
     * These listeners will be added when the polyfill first loads, and anytime
     * the window is blurred, so that they are active when the window regains
     * focus.
     */
    function addInitialPointerMoveListeners() {
      if (typeof document === "undefined") return;
      document.addEventListener("mousemove", onInitialPointerMove);
      document.addEventListener("mousedown", onInitialPointerMove);
      document.addEventListener("mouseup", onInitialPointerMove);
      document.addEventListener("pointermove", onInitialPointerMove);
      document.addEventListener("pointerdown", onInitialPointerMove);
      document.addEventListener("pointerup", onInitialPointerMove);
      document.addEventListener("touchmove", onInitialPointerMove);
      document.addEventListener("touchstart", onInitialPointerMove);
      document.addEventListener("touchend", onInitialPointerMove);
    }

    function removeInitialPointerMoveListeners() {
      if (typeof document === "undefined") return;
      document.removeEventListener("mousemove", onInitialPointerMove);
      document.removeEventListener("mousedown", onInitialPointerMove);
      document.removeEventListener("mouseup", onInitialPointerMove);
      document.removeEventListener("pointermove", onInitialPointerMove);
      document.removeEventListener("pointerdown", onInitialPointerMove);
      document.removeEventListener("pointerup", onInitialPointerMove);
      document.removeEventListener("touchmove", onInitialPointerMove);
      document.removeEventListener("touchstart", onInitialPointerMove);
      document.removeEventListener("touchend", onInitialPointerMove);
    }

    /**
     * If the most recent user interaction was via the keyboard;
     * and the key press did not include a meta, alt/option, or control key;
     * then the modality is keyboard. Otherwise, the modality is not keyboard.
     * Apply `focus-visible` to any current active element and keep track
     * of our keyboard modality state with `hadKeyboardEvent`.
     */
    function onKeyDown(e: KeyboardEvent) {
      if (e.metaKey || e.altKey || e.ctrlKey) return;

      const target = e.target as Element | null;
      const editable = isEditable(target);

      if (editable) return;

      document.body.classList.add("keyboard-user");
      setKeyboardUser(true);
    }
    /**
     * If the user changes tabs, keep track of whether or not the previously
     * focused element had .focus-visible.
     */
    function onVisibilityChange() {
      if (document.visibilityState === "hidden") {
        // If the tab becomes active again, the browser will handle calling focus
        // on the element (Safari actually calls it twice).
        // If this tab change caused a blur on an element with focus-visible,
        // re-apply the class when the user switches back to the tab.
        document.body.classList.add("keyboard-user");
        setKeyboardUser(true);

        addInitialPointerMoveListeners();
      }
    }

    // For some kinds of state, we are interested in changes at the global scope
    // only. For example, global pointer input, global key presses and global
    // visibility change should affect the state at every scope:
    if (typeof document === "undefined") return;
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("mousedown", onPointerDown, true);
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("touchstart", onPointerDown, true);
    document.addEventListener("visibilitychange", onVisibilityChange, true);

    addInitialPointerMoveListeners();

    return () => {
      if (typeof document === "undefined") return;
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("mousedown", onPointerDown, true);
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("touchstart", onPointerDown, true);
      document.removeEventListener("visibilitychange", onVisibilityChange, true);

      removeInitialPointerMoveListeners();
    };
  }, [setKeyboardUser]);
}
